import { useMemo, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { type AxisDraft, type CellMap, CompetencyMatrix } from "./competency-matrix";

/**
 * CSV import for a discipline's vertical competencies.
 *
 * Contract (shown to the user in the panel):
 *   - Each COLUMN is a vertical competency (the header row holds the labels).
 *   - Each data ROW is a level, in order from the model's lowest level up.
 *   - Optionally the first column may be a level label ("Nível" header, or
 *     L0/L1/0/1 values) — it's auto-detected and stripped.
 *   - The number of data rows must equal the model's level count.
 *
 * On a successful parse the panel shows the resulting matrix (editable) so the
 * operator can review/adjust before committing. "Adicionar disciplina" emits a
 * draft the parent appends to its discipline list.
 */

export interface DisciplineImportDraft {
  label: string;
  description: string;
  axes: AxisDraft[];
  cells: CellMap;
}

export interface DisciplineCsvImportProps {
  /** The model's level range, in order (e.g. [0,1,…,7]). */
  levels: number[];
  disabled: boolean;
  onImport: (d: DisciplineImportDraft) => void;
  onCancel: () => void;
}

/** Minimal RFC-4180 CSV parser — handles quoted fields with embedded commas,
 *  newlines, and "" escapes. Returns rows of string fields. */
export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let field = "";
  let row: string[] = [];
  let i = 0;
  let inQuotes = false;
  const n = text.length;
  // Normalize CRLF → LF so quoted newlines stay LF-only.
  const s = text.replace(/\r\n?/g, "\n");
  const len = s.length || n;
  while (i < len) {
    const c = s[i];
    if (inQuotes) {
      if (c === '"') {
        if (s[i + 1] === '"') {
          field += '"';
          i += 2;
        } else {
          inQuotes = false;
          i += 1;
        }
      } else {
        field += c;
        i += 1;
      }
    } else if (c === '"') {
      inQuotes = true;
      i += 1;
    } else if (c === ",") {
      row.push(field);
      field = "";
      i += 1;
    } else if (c === "\n") {
      row.push(field);
      rows.push(row);
      row = [];
      field = "";
      i += 1;
    } else {
      field += c;
      i += 1;
    }
  }
  // Flush the last field/row (no trailing newline).
  if (field.length > 0 || row.length > 0) {
    row.push(field);
    rows.push(row);
  }
  // Drop fully-empty rows (e.g. trailing blank line).
  return rows.filter((r) => r.some((cell) => cell.trim().length > 0));
}

const LEVEL_LABEL_RE = /^\s*l?\d+\s*$/i;
const LEVEL_HEADER_RE = /^\s*(n[íi]vel|level)\s*$/i;

type ParseResult = { ok: true; axes: AxisDraft[]; cells: CellMap } | { ok: false; error: string };

/** Parse a CSV into discipline axes (columns) + cells (rows = levels). */
export function parseDisciplineCsv(text: string, levels: number[]): ParseResult {
  const rows = parseCsv(text);
  if (rows.length < 2) {
    return {
      ok: false,
      error:
        "CSV precisa de pelo menos um cabeçalho (nomes das competências) e uma linha de nível.",
    };
  }
  let header = rows[0].map((c) => c.trim());
  let dataRows = rows.slice(1);

  // Detect + strip an optional leading level column.
  const headerLooksLevel =
    header.length > 0 && (header[0] === "" || LEVEL_HEADER_RE.test(header[0]));
  const dataLooksLevel = dataRows.every((r) => r.length > 0 && LEVEL_LABEL_RE.test(r[0] ?? ""));
  if (headerLooksLevel || dataLooksLevel) {
    header = header.slice(1);
    dataRows = dataRows.map((r) => r.slice(1));
  }

  const labels = header.map((h) => h.trim());
  if (labels.length === 0 || labels.every((l) => l.length === 0)) {
    return { ok: false, error: "Nenhuma competência encontrada no cabeçalho do CSV." };
  }
  if (labels.some((l) => l.length === 0)) {
    return { ok: false, error: "Toda coluna de competência precisa de um nome no cabeçalho." };
  }
  const dupes = labels.filter((l, idx) => labels.indexOf(l) !== idx);
  if (dupes.length > 0) {
    return { ok: false, error: `Competências duplicadas no cabeçalho: ${dupes.join(", ")}.` };
  }
  if (dataRows.length !== levels.length) {
    return {
      ok: false,
      error: `O CSV tem ${dataRows.length} linha(s) de nível, mas o modelo tem ${levels.length} nível(is) (L${levels[0]}–L${levels[levels.length - 1]}). Ajuste as linhas pra bater com os níveis do modelo.`,
    };
  }

  const axes: AxisDraft[] = labels.map((label) => ({ label, description: "", weight: 1 }));
  const cells: CellMap = {};
  for (let ai = 0; ai < labels.length; ai++) {
    cells[ai] = {};
    for (let ri = 0; ri < dataRows.length; ri++) {
      const value = (dataRows[ri][ai] ?? "").trim();
      if (value.length > 0) cells[ai][levels[ri]] = value;
    }
  }
  return { ok: true, axes, cells };
}

export function DisciplineCsvImport(props: DisciplineCsvImportProps) {
  const { levels, disabled } = props;
  const fileRef = useRef<HTMLInputElement>(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [csvText, setCsvText] = useState("");
  const [error, setError] = useState<string | null>(null);
  // Editable preview built from the parsed CSV.
  const [preview, setPreview] = useState<{ axes: AxisDraft[]; cells: CellMap } | null>(null);

  const exampleHeader = useMemo(
    () =>
      `Nível,Programação,Arquitetura\n${levels
        .map((l) => `L${l},desc da programação em L${l},desc da arquitetura em L${l}`)
        .join("\n")}`,
    [levels],
  );

  function handleParse() {
    setError(null);
    const res = parseDisciplineCsv(csvText, levels);
    if (!res.ok) {
      setPreview(null);
      setError(res.error);
      return;
    }
    setPreview({ axes: res.axes, cells: res.cells });
  }

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    setCsvText(text);
    setError(null);
    setPreview(null);
  }

  function confirmImport() {
    if (!preview) return;
    if (name.trim().length === 0) {
      setError("Dê um nome à disciplina antes de adicionar.");
      return;
    }
    props.onImport({
      label: name.trim(),
      description: description.trim(),
      axes: preview.axes,
      cells: preview.cells,
    });
  }

  // Preview-edit handlers (let the operator tweak before committing).
  function updatePreviewAxis(idx: number, patch: Partial<AxisDraft>) {
    setPreview((p) =>
      p ? { ...p, axes: p.axes.map((a, i) => (i === idx ? { ...a, ...patch } : a)) } : p,
    );
  }
  function setPreviewCell(axisIdx: number, level: number, value: string) {
    setPreview((p) => {
      if (!p) return p;
      const cells = { ...p.cells };
      cells[axisIdx] = { ...(cells[axisIdx] ?? {}), [level]: value };
      return { ...p, cells };
    });
  }

  return (
    <div
      className="card"
      data-testid="discipline-csv-import"
      style={{ padding: 20, marginBottom: 16, borderColor: "var(--r-ink-faint, #c7c2bb)" }}
    >
      <div
        style={{
          fontSize: 14,
          fontWeight: 600,
          color: "var(--r-ink)",
          marginBottom: 4,
        }}
      >
        Importar disciplina de CSV
      </div>
      <p style={{ fontSize: 12, color: "var(--r-ink-muted)", lineHeight: 1.5, margin: "0 0 12px" }}>
        Cada <strong>coluna</strong> é uma competência vertical; cada <strong>linha</strong> é um
        nível (na ordem L{levels[0]}–L{levels[levels.length - 1]}). A primeira coluna pode ser o
        rótulo do nível (opcional — é detectada e ignorada). O número de linhas de dados deve bater
        com os {levels.length} níveis do modelo.
      </p>

      <div style={{ display: "grid", gridTemplateColumns: "minmax(200px, 280px) 1fr", gap: 12 }}>
        <FieldLabel label="Nome da disciplina">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Engenharia"
            disabled={disabled}
            data-testid="csv-discipline-name"
          />
        </FieldLabel>
        <FieldLabel label="Descrição (opcional)">
          <Input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Competências técnicas verticais de engenharia."
            disabled={disabled}
            data-testid="csv-discipline-description"
          />
        </FieldLabel>
      </div>

      <div style={{ marginTop: 12 }}>
        <FieldLabel label="CSV">
          <textarea
            value={csvText}
            onChange={(e) => {
              setCsvText(e.target.value);
              setPreview(null);
              setError(null);
            }}
            placeholder={exampleHeader}
            rows={6}
            disabled={disabled}
            data-testid="csv-text"
            spellCheck={false}
            style={{
              width: "100%",
              fontFamily: "ui-monospace, SFMono-Regular, monospace",
              fontSize: 12,
              lineHeight: 1.5,
              padding: 12,
              border: "1px solid var(--r-ink-faint, #e9e3d8)",
              borderRadius: 6,
              background: "var(--r-bg, #fffaf3)",
              color: "var(--r-ink)",
              resize: "vertical",
            }}
          />
        </FieldLabel>
      </div>

      <div
        style={{ display: "flex", gap: 8, marginTop: 12, alignItems: "center", flexWrap: "wrap" }}
      >
        <input
          ref={fileRef}
          type="file"
          accept=".csv,text/csv"
          onChange={handleFile}
          disabled={disabled}
          data-testid="csv-file"
          style={{ display: "none" }}
        />
        <button
          type="button"
          className="btn ghost"
          onClick={() => fileRef.current?.click()}
          disabled={disabled}
          data-testid="csv-pick-file"
        >
          Escolher arquivo .csv
        </button>
        <button
          type="button"
          className="btn ghost"
          onClick={handleParse}
          disabled={disabled || csvText.trim().length === 0}
          data-testid="csv-validate"
        >
          Validar e prever matriz
        </button>
        <button
          type="button"
          className="btn ghost"
          onClick={props.onCancel}
          disabled={disabled}
          data-testid="csv-cancel"
          style={{ marginLeft: "auto" }}
        >
          Cancelar
        </button>
      </div>

      {error && (
        <div
          data-testid="csv-error"
          style={{
            marginTop: 12,
            padding: "10px 14px",
            borderRadius: 6,
            background: "var(--r-bg-soft, #fff8ec)",
            border: "1px solid var(--r-amber, #b07811)",
            color: "var(--r-ink)",
            fontSize: 13,
            lineHeight: 1.5,
          }}
        >
          {error}
        </div>
      )}

      {preview && (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 12, color: "var(--r-ink-muted)", marginBottom: 8 }}>
            Pré-visualização — {preview.axes.length} competência(s) × {levels.length} nível(is).
            Revise/ajuste e adicione.
          </div>
          <CompetencyMatrix
            axes={preview.axes}
            cells={preview.cells}
            levels={levels}
            disabled={disabled}
            testidPrefix="csv-preview"
            onAddAxis={() =>
              setPreview((p) =>
                p ? { ...p, axes: [...p.axes, { label: "", description: "", weight: 1 }] } : p,
              )
            }
            onRemoveAxis={(idx) =>
              setPreview((p) => (p ? { ...p, axes: p.axes.filter((_, i) => i !== idx) } : p))
            }
            onUpdateAxis={updatePreviewAxis}
            onSetCell={setPreviewCell}
          />
          <div style={{ marginTop: 12, display: "flex", justifyContent: "flex-end" }}>
            <button
              type="button"
              className="btn primary"
              onClick={confirmImport}
              disabled={disabled}
              data-testid="csv-confirm"
            >
              Adicionar disciplina
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function FieldLabel({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div style={{ fontSize: 12, fontWeight: 600, color: "var(--r-ink)", marginBottom: 4 }}>
        {label}
      </div>
      {children}
    </div>
  );
}
